import React from "react";
import clsx from "clsx";

import {
    TextField as UIKitTextField,
    type TextFieldProps as UIKitTextFieldProps,
} from "@proda-ai/ui-kit";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";

export type TextFieldProps = JSX.IntrinsicAttributes &
    Omit<UIKitTextFieldProps, "label" | "children"> & {
        label?: UIKitTextFieldProps["label"];
        sx?: BoxProps["sx"];
    };

const alignInputBorderStyles = {
    "& input": {
        border: "1px solid var(--proda-white-150) !important",
    },
};

const alignDisabledInputStyles = (disabled?: TextFieldProps["disabled"]) => ({
    "& input:read-only": {
        backgroundColor: disabled === true ? "var(--proda-white-130)" : "white",
        cursor: disabled === true ? "not-allowed" : "pointer",
    },
});

const allowInputsWithoutLabel = (label?: TextFieldProps["label"]) =>
    label === undefined
        ? {
              "& label, & .uikit-field-label, & .uikit-text-field__label": {
                  display: "none",
              },
          }
        : ({} as Record<string, never>);

const testId = "TextField";
const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
    (props, ref) => {
        const { sx, label, disabled, className, ...otherProps } = props;

        return (
            <Box
                ref={ref}
                data-testid={testId}
                className={clsx([testId, className])}
                component={UIKitTextField}
                label={label ?? ""}
                disabled={disabled}
                sx={{
                    ...alignInputBorderStyles,
                    ...allowInputsWithoutLabel(label),
                    ...alignDisabledInputStyles(disabled),
                    ...sx,
                }}
                {...otherProps}
            />
        );
    },
);

TextField.displayName = "TextField";

export default TextField;
